import React, { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  ButtonBase,
  CircularProgress,
  Container,
  Snackbar,
  Stack,
  Typography,
} from '@mui/material';
import MuiBox from '@mui/material/Box';
import { Canvas, useFrame } from '@react-three/fiber';
import { Environment, OrbitControls, useGLTF } from '@react-three/drei';
import { Box3, Group, MathUtils, SRGBColorSpace, ACESFilmicToneMapping, Vector3 } from 'three';
import { SkeletonUtils } from 'three-stdlib';
import { useNavigate } from 'react-router-dom';

import Bottom from '../Bottom';
// 장기 아이콘 — 빨간(활성)/파란(비활성) 2버전
import iconBrainRed from '../../assets/icon/iconBrainRed.png';
import iconBrainBlue from '../../assets/icon/iconBrainBlue.png';
import iconHeartRed from '../../assets/icon/iconHeartRed.png';
import iconHeartBlue from '../../assets/icon/iconHeartBlue.png';
import iconKidneysRed from '../../assets/icon/iconKidneysRed.png';
import iconKidneysBlue from '../../assets/icon/iconKidneysBlue.png';
import iconLiverRed from '../../assets/icon/iconLiverRed.png';
import iconLiverBlue from '../../assets/icon/iconLiverBlue.png';
import iconIntestineRed from '../../assets/icon/iconIntestineRed.png';
import iconIntestineBlue from '../../assets/icon/iconIntestineBlue.png';
import iconPancreasRed from '../../assets/icon/iconPancreasRed.png';
import iconPancreasBlue from '../../assets/icon/iconPancreasBlue.png';
// 질환 카드 아이콘은 DigitalTwinShared.DISEASE_CARD_DEFS 로 옮겼다 (화면 1·2 공용)
import DigitalTwinScreen4 from './DigitalTwinScreen4';
import DiseaseSurveySheet from './DiseaseSurveySheet';
import {
  Disclaimer,
  DiseaseRisk,
  DRACO_PATH,
  ORGAN_MODEL_MAP,
  OrganModelViewer,
  buildDiseaseCards,
  useBodyModelPath,
} from './DigitalTwinShared';
import {
  SurveyAnswers,
  countAllAnswered,
  createEmptyAnswers,
  useSurveyStatus,
} from './diseaseSurvey';
import {
  ORGAN_KO_NAME,
  VitalMetricView,
  buildVitalMetrics,
  hasSubmittedSurvey,
  isOrganAtRisk,
  useDigitalTwin,
} from './digitalTwinApi';

// TS2590 회피: 이 파일에서만 Box를 any로 사용
const Box = MuiBox as any;

// 전신 모델 경로는 접속 계정의 성별로 결정된다 —
// DigitalTwinShared.useBodyModelPath 참고 (여기서 고정하지 않는다).

type Expression = 'neutral' | 'sad';

type OrganButton = {
  value: string;
  iconRed: string;
  iconBlue: string;
  label: string;
};

const STATUS_COLOR: Record<DiseaseRisk['status'], string> = {
  '나쁨': '#ed5454',
  '주의': '#ff8000',
  '양호': '#38c172',
};

// 3D 모델 표면 위 6개 장기 위치 마커 (모델 영역 기준 백분율 — 숫자)
//   x: 가로 위치 (작을수록 왼쪽 / 클수록 오른쪽)
//   y: 세로 위치 (작을수록 위쪽 / 클수록 아래쪽)
//   마커 색(위험=빨강 / 정상=하늘색)은 AP_009_002 의 장기 등급에서 파생된다 — 여기서 고정하지 않는다.
type OrganMarker = { organ: string; x: number; y: number };
const ORGAN_MARKERS: OrganMarker[] = [
  { organ: 'brain',     x: 50, y: 13 }, // 뇌 - 머리
  { organ: 'heart',     x: 54, y: 37 }, // 심장 - 가슴
  { organ: 'liver',     x: 44, y: 36 }, // 간   ← x/y 조정해서 간 위치로 맞추세요
  { organ: 'pancreas',  x: 52, y: 39 }, // 췌장 ← x/y 조정해서 췌장 위치로 맞추세요
  { organ: 'kidneys',   x: 47, y: 42 }, // 신장 ← x/y 조정해서 신장 위치로 맞추세요
  { organ: 'intestine', x: 50, y: 46 }, // 대장 ← x/y 조정해서 대장 위치로 맞추세요
];

// 레이아웃 상수 (연결선 좌표 계산용)
const ORGAN_ORDER = ['brain', 'heart', 'kidneys', 'liver', 'intestine', 'pancreas'];
const ORGAN_RAIL_TOP = 50;
const ORGAN_BOX_SIZE = 51;
const ORGAN_BOX_GAP = 11.6;        // Stack spacing 1.45 ≈ 11.6px
const STAGE_HEIGHT = 636;           // 최대(기준) 높이 — 아이폰 14 Pro Max 급 큰 화면 기준
const STAGE_WIDTH = 398;           // 컨테이너 maxWidth 430 - mx(16*2)
const MODEL_LEFT = 36;
const MODEL_RIGHT = -40;
const MODEL_TOP = -2;

// ── 3D 스테이지 반응형 높이 ─────────────────────────────────────────────
// 스테이지 높이를 고정하면 세로가 짧은 기기(아이폰 SE 등)에서 모델이 화면을
// 거의 다 채워 답답해 보인다. 뷰포트 높이에 비례시키되 아래로 잘라(clamp) 준다.
//  - 큰 화면(14 Pro Max): 636px 유지  - 작은 화면(SE): 비례해 축소
// 마커·장기 모델은 스테이지 기준 백분율로 배치되므로 높이가 줄어도 함께 맞춰진다.
// 좌측 장기 버튼 레일(px 고정, 하단 ≈414px)은 최소 높이 430px 안에 들어온다.
const STAGE_HEIGHT_MIN = 430;
const STAGE_VH_RATIO = 0.78;        // 14 Pro Max 에서 636px 가 나오는 비율

const computeStageHeight = (): number => {
  if (typeof window === 'undefined') return STAGE_HEIGHT;
  const vh = window.innerHeight || 900;
  return Math.round(Math.max(STAGE_HEIGHT_MIN, Math.min(STAGE_HEIGHT, vh * STAGE_VH_RATIO)));
};

/** 뷰포트 높이에 맞춘 스테이지 높이(px). 회전·리사이즈 시 갱신된다. */
const useStageHeight = (): number => {
  const [h, setH] = useState<number>(computeStageHeight);
  useEffect(() => {
    const onResize = () => setH(computeStageHeight());
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);
  return h;
};

// 마커의 부모(스테이지) 기준 px 좌표
const markerPx = (m: OrganMarker) => {
  const areaW = STAGE_WIDTH - MODEL_LEFT - MODEL_RIGHT; // 모델 Canvas 너비
  const areaH = STAGE_HEIGHT - MODEL_TOP;
  return {
    x: MODEL_LEFT + (m.x / 100) * areaW,
    y: MODEL_TOP + (m.y / 100) * areaH,
  };
};

// 장기 버튼의 부모(스테이지) 기준 px 중심 좌표
const buttonPx = (idx: number) => ({
  x: 2 + ORGAN_BOX_SIZE / 2,
  y: ORGAN_RAIL_TOP + idx * (ORGAN_BOX_SIZE + ORGAN_BOX_GAP) + ORGAN_BOX_SIZE / 2,
});

const modelLoading = (
  <Box
    sx={{
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#8aa2ad',
    }}
  >
    <CircularProgress size={28} sx={{ color: '#8aa2ad' }} />
  </Box>
);

const MyModel = ({
  expression,
  spinSignal,
  modelPath,
}: {
  expression: Expression;
  spinSignal: number;
  modelPath: string;
}) => {
  const { scene } = useGLTF(modelPath, DRACO_PATH);
  const model = useMemo(() => SkeletonUtils.clone(scene) as Group, [scene]);
  const morphTargetsRef = useRef<{ influences: number[]; index: number }[]>([]);
  // 한 바퀴 회전 애니메이션 상태
  const spinRef = useRef<{ active: boolean; t: number; from: number }>({ active: false, t: 0, from: 0 });
  const firstSpinRef = useRef(true);

  // spinSignal 변경 시 한 바퀴 회전 시작 (첫 마운트는 제외)
  useEffect(() => {
    if (firstSpinRef.current) {
      firstSpinRef.current = false;
      return;
    }
    spinRef.current = { active: true, t: 0, from: model.rotation.y };
  }, [spinSignal, model]);

  useEffect(() => {
    morphTargetsRef.current = [];

    model.traverse((child: any) => {
      if (!child.isMesh) return;

      child.castShadow = true;
      child.receiveShadow = true;

      const mats = Array.isArray(child.material) ? child.material : [child.material];
      mats.forEach((mat: any) => {
        if ('envMapIntensity' in mat) mat.envMapIntensity = 0.3;
        mat.needsUpdate = true;
      });

      const dict = child.morphTargetDictionary;
      if (dict && child.morphTargetInfluences) {
        const sadnessIndex = dict['슬픔'];
        if (typeof sadnessIndex === 'number') {
          morphTargetsRef.current.push({
            influences: child.morphTargetInfluences,
            index: sadnessIndex,
          });
        }
      }
    });

    const box = new Box3().setFromObject(model);
    const size = box.getSize(new Vector3());
    const center = box.getCenter(new Vector3());
    const desiredHeight = 2.7;
    const scale = size.y ? desiredHeight / size.y : 1;

    model.scale.setScalar(scale);

    // 모델의 정확한 중심을 (0, 0, 0)에 배치 -> 카메라가 모델 중앙을 정조준
    const scaledBox = new Box3().setFromObject(model);
    const scaledCenter = scaledBox.getCenter(center);
    model.position.x -= scaledCenter.x;
    model.position.y -= scaledCenter.y;
    model.position.z -= scaledCenter.z;
  }, [model]);

  useFrame((_, delta) => {
    const target = expression === 'sad' ? 1 : 0;
    const lerpFactor = Math.min(1, delta * 4);
    morphTargetsRef.current.forEach(({ influences, index }) => {
      const current = influences[index] ?? 0;
      influences[index] = MathUtils.lerp(current, target, lerpFactor);
    });

    // 한 바퀴 회전 (오른쪽 방향, 3초 — 빛줄기와 동기)
    const spin = spinRef.current;
    if (spin.active) {
      spin.t += delta / 3;
      const p = Math.min(1, spin.t);
      // easeInOutQuad
      const eased = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
      model.rotation.y = spin.from - eased * Math.PI * 2; // 오른쪽 회전
      if (p >= 1) {
        spin.active = false;
        model.rotation.y = spin.from;
      }
    }
  });

  return <primitive object={model} />;
};

// 모듈 로드 시점에는 성별을 알 수 없으므로 preload 하지 않는다
// (DigitalTwinShared 의 같은 주석 참고).

const ModelViewer = ({
  expression,
  spinSignal,
  frozen,
  rotateEl,
}: {
  expression: Expression;
  spinSignal: number;
  frozen: boolean;
  // 회전 제스처를 받을 DOM 영역. 넘기면 캔버스 전체가 아니라 이 영역에서만 회전한다
  // (그 바깥은 터치가 페이지로 흘러가 스크롤됨). 없으면 캔버스 전체(기본).
  rotateEl?: HTMLElement | null;
}) => {
  const controlsRef = useRef<any>(null);
  const modelPath = useBodyModelPath();

  // 장기 선택/전환/복귀(spinSignal 변경) 시마다 카메라를 정면으로 리셋
  // → 사용자가 드래그로 돌려놨어도 항상 정면을 본 상태에서 회전이 시작됨
  useEffect(() => {
    const c = controlsRef.current;
    if (c) {
      c.setAzimuthalAngle(0);
      c.setPolarAngle(Math.PI / 2);
      c.update();
    }
  }, [spinSignal]);

  return (
    <Canvas
      shadows="soft"
      dpr={[1, 2]}
      camera={{ position: [0, 0, 5.25], fov: 31 }}
      gl={{
        alpha: true,
        antialias: true,
        outputColorSpace: SRGBColorSpace,
        toneMapping: ACESFilmicToneMapping,
        toneMappingExposure: 1.02,
      }}
    >
      <ambientLight intensity={0.65} />
      <directionalLight position={[2, 5, 4]} intensity={1.1} />
      <directionalLight position={[-3, 3, 2]} intensity={0.25} />
      <Environment preset="apartment" />
      <Suspense fallback={null}>
        <group position={[0, 0, 0]}>
          {/* 성별 확정 전에는 로드하지 않는다 (useBodyModelPath 주석 참고) */}
          {modelPath && (
            <MyModel expression={expression} spinSignal={spinSignal} modelPath={modelPath} />
          )}
        </group>
      </Suspense>
      <OrbitControls
        ref={controlsRef}
        target={[0, 0, 0]}
        // 회전 제스처를 받을 영역 — 지정하면 이 영역에서만 회전(그 밖은 스크롤)
        domElement={rotateEl ?? undefined}
        enablePan={false}
        enableZoom={false}
        // 장기 선택 상태(frozen)에서는 좌우 회전 불가 — 첫 화면에서만 회전 가능
        enableRotate={!frozen}
        enableDamping
        dampingFactor={0.08}
        minPolarAngle={Math.PI / 2}
        maxPolarAngle={Math.PI / 2}
      />
    </Canvas>
  );
};

/**
 * 좌측 장기 버튼 레일.
 *  - 기본(미선택): 파란 박스 + 볼록(튀어나온) 그림자
 *  - 선택: 빨간 박스 + 오목(들어간) inset 그림자
 */
const OrganRail = ({
  organs,
  selectedOrgan,
  onSelect,
  riskOrgans,
}: {
  organs: OrganButton[];
  selectedOrgan: string | null;
  onSelect: (organ: OrganButton) => void;
  /** AP_009_002 에서 Grade 0 이 아닌 장기 키. 미조회·조회 실패 시 빈 Set. */
  riskOrgans: Set<string>;
}) => (
  <Stack
    spacing={1.45}
    sx={{
      position: 'absolute',
      left: 2,
      top: `${ORGAN_RAIL_TOP}px`,
      zIndex: 4,
    }}
  >
    {organs.map((organ) => {
      // 배경색은 서버 판단 결과 — 위험 등급(Grade 1+) 장기만 빨강 (선택과 무관)
      //  조회 전·실패 시에는 riskOrgans 가 비어 있어 전부 파랑으로 남는다.
      //  임의로 빨강을 칠하면 근거 없는 위험 표시가 되므로 그렇게 하지 않는다.
      const isRed = riskOrgans.has(organ.value);
      // 선택된 버튼은 눌린(들어간) 상태
      const isPressed = organ.value === selectedOrgan;
      return (
        <ButtonBase
          key={organ.value}
          aria-label={organ.label}
          onClick={() => onSelect(organ)}
          sx={{
            width: ORGAN_BOX_SIZE,
            height: ORGAN_BOX_SIZE,
            borderRadius: '15px',
            border: '1px solid rgba(255,255,255,.5)',
            bgcolor: isRed ? '#f5dcdd' : '#e8ecf1',
            // 선택: 오목(들어감) / 미선택: 볼록(튀어나옴)
            boxShadow: isPressed
              ? `inset 4px 4px 4px ${isRed ? '#daafb1' : '#C5D3DA'}`
              : isRed
              ? '4px 4px 4px #daafb1'
              : '4px 4px 4px #c5d3da',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            transition: 'background-color .25s ease, box-shadow .25s ease, transform .12s ease',
            '&:active': { transform: 'scale(.96)' },
          }}
        >
          <Avatar
            alt=""
            src={isRed ? organ.iconRed : organ.iconBlue}
            variant="square"
            sx={{
              width: 35,
              height: 35,
              position: 'relative',
              zIndex: 5,
              '& img': { objectFit: 'contain' },
            }}
          />
        </ButtonBase>
      );
    })}
  </Stack>
);

// 마커 크기 상수 (빨강·하늘색 동일)
const MARKER_MAIN = 16;   // 앞(메인) 원
const MARKER_HALO = 24;   // 뒤 원 (메인보다 조금 큼)

/**
 * 3D 모델 표면의 장기 위치 마커.
 *  - 왼쪽 장기 버튼을 눌러 선택한 장기의 마커만 연결선과 함께 표시
 *  - 마커 = 앞 원(메인) + 뒤 원(50% 흐림, 조금 큼) 동심원 + 퍼지는 ping
 *  - 마커(원)를 클릭하면 해당 장기 화면 2로 이동
 */
const RiskMarkers = ({
  selectedOrgan,
  onMarkerClick,
  riskOrgans,
}: {
  selectedOrgan: string | null;
  onMarkerClick: (organ: string) => void;
  riskOrgans: Set<string>;
}) => (
  <Box
    sx={{
      position: 'absolute',
      inset: `${MODEL_TOP}px ${MODEL_RIGHT}px 0 ${MODEL_LEFT}px`,
      pointerEvents: 'none',
      zIndex: 3,
    }}
  >
    {ORGAN_MARKERS.filter((m) => m.organ === selectedOrgan).map((marker) => {
      const isSel = true; // 선택된 장기만 렌더되므로 항상 true
      const isRed = riskOrgans.has(marker.organ);
      // 위험 등급이 아닌 장기는 초록(#38c172 = 56,193,114) — '양호' 배지와 같은 색.
      // 예전 흰색은 밝은 장기 모델·배경 위에서 마커가 묻혀 잘 보이지 않았다.
      const mainColor = isRed ? '#ed5454' : 'rgba(56,193,114,.95)';
      // 뒤 원 — 메인 색의 50% 투명도
      const haloColor = isRed ? 'rgba(237,84,84,.5)' : 'rgba(56,193,114,.5)';
      const pingColor = isRed ? 'rgba(237,84,84,.7)' : 'rgba(56,193,114,.8)';

      // 퍼짐 효과용 sx (transform 기본값 + fill-mode로 위치 점프 방지)
      const pingSx = (delay: string) => ({
        position: 'absolute' as const,
        left: 0,
        top: 0,
        width: MARKER_HALO,
        height: MARKER_HALO,
        borderRadius: '50%',
        bgcolor: pingColor,
        transform: 'translate(-50%, -50%)',
        animation: `organPing 1.6s ease-out ${delay} infinite both`,
        '@keyframes organPing': {
          '0%': { transform: 'translate(-50%, -50%) scale(1)', opacity: 0.85 },
          '60%': { opacity: 0.3 },
          '100%': { transform: 'translate(-50%, -50%) scale(2.8)', opacity: 0 },
        },
      });

      return (
        <Box
          key={marker.organ}
          sx={{
            position: 'absolute',
            // 원을 장기 모델 정중앙(마커 위치)에 배치
            left: `${marker.x}%`,
            top: `${marker.y}%`,
            width: 0,
            height: 0,
            // 빛줄기(3s) → 장기 모델(4s) 뒤에 원이 나타남
            animation: 'markerAppear .5s ease 4s both',
            '@keyframes markerAppear': {
              from: { opacity: 0 },
              to: { opacity: 1 },
            },
          }}
        >
          {/* 퍼지는 효과 (선택 시) — 2겹 시차 */}
          {isSel && (
            <>
              <Box sx={pingSx('0s')} />
              <Box sx={pingSx('.8s')} />
            </>
          )}

          {/* 뒤 원 (메인보다 조금 큼, 50% 흐림) */}
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: MARKER_HALO,
              height: MARKER_HALO,
              borderRadius: '50%',
              bgcolor: haloColor,
              transform: 'translate(-50%, -50%)',
            }}
          />

          {/* 앞 원 (메인) + 클릭 영역 */}
          <Box
            role="button"
            aria-label={`${marker.organ} 상세`}
            onClick={() => onMarkerClick(marker.organ)}
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: 36,
              height: 36,
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'auto',
              cursor: 'pointer',
            }}
          >
            <Box
              sx={{
                width: MARKER_MAIN,
                height: MARKER_MAIN,
                borderRadius: '50%',
                bgcolor: mainColor,
                // 그림자도 마커 색을 따른다 (빨강/하늘색)
                boxShadow: isRed
                  ? '0 2px 8px rgba(237,84,84,.3)'
                  : '0 2px 8px rgba(56,193,114,.35)',
              }}
            />
          </Box>

        </Box>
      );
    })}
  </Box>
);

// 장기 모델 Canvas 크기 / 원을 모델 왼쪽 끝으로 옮길 오프셋
const ORGAN_MODEL_SIZE = 150;
const CIRCLE_OFFSET_X = 62; // 마커 기준 왼쪽으로 이동(px) — 모델 왼쪽 끝에 원이 붙도록

/**
 * 선택된 장기의 3D 모델 레이어.
 *  - 캐릭터(z1)와 연결선·원(z3) 사이(z2)에 배치 → 선·원이 장기 모델 위에 보임
 *  - 모델은 마커 위치를 중심으로 배치, 나타날 때 천천히 페이드인
 */
const OrganModelLayer = ({
  selectedOrgan,
  onModelClick,
}: {
  selectedOrgan: string | null;
  onModelClick: (organ: string) => void;
}) => {
  if (!selectedOrgan) return null;
  const marker = ORGAN_MARKERS.find((m) => m.organ === selectedOrgan);
  const model = ORGAN_MODEL_MAP[selectedOrgan];
  if (!marker || !model) return null;
  return (
    <Box
      sx={{
        position: 'absolute',
        inset: `${MODEL_TOP}px ${MODEL_RIGHT}px 0 ${MODEL_LEFT}px`,
        pointerEvents: 'none',
        zIndex: 2,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          left: `${marker.x}%`,
          top: `${marker.y}%`,
          width: ORGAN_MODEL_SIZE,
          height: ORGAN_MODEL_SIZE,
          transform: 'translate(-50%, -50%)', // 마커 위치 중심
          // 빛줄기(3s)가 끝나 사라진 뒤부터 나타남
          animation: 'organFadeIn 1s ease 3s both',
          '@keyframes organFadeIn': {
            from: { opacity: 0 },
            to: { opacity: 1 },
          },
        }}
      >
        <OrganModelViewer
          path={model.path}
          organKey={selectedOrgan ?? undefined}
          gradeOverride={0}
        />
        {/* 장기 모델을 눌러도 마커(원)와 동일하게 화면 2로 이동한다.
            부모가 pointerEvents:none 이라 이 오버레이만 auto 로 클릭을 받는다. */}
        <Box
          role="button"
          aria-label={`${selectedOrgan} 상세`}
          onClick={() => onModelClick(selectedOrgan)}
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'auto',
            cursor: 'pointer',
          }}
        />
      </Box>
    </Box>
  );
};

/**
 * 선택된 장기 버튼 ↔ 해당 장기 마커를 잇는 연결선.
 * 선 색상은 선택된 버튼 색(빨강)을 따름.
 */
// 현재 JSX 에서 호출되지 않는 컴포넌트(미사용). 되살릴 때 riskOrgans 를 넘기면 된다.
const OrganConnector = ({
  selectedOrgan,
  riskOrgans,
}: {
  selectedOrgan: string | null;
  riskOrgans: Set<string>;
}) => {
  if (!selectedOrgan) return null;
  const marker = ORGAN_MARKERS.find((m) => m.organ === selectedOrgan);
  const idx = ORGAN_ORDER.indexOf(selectedOrgan);
  if (!marker || idx < 0) return null;
  const b = buttonPx(idx);
  const mkRaw = markerPx(marker);
  // 원이 왼쪽으로 이동했으므로 선 끝점도 동일하게 보정
  const mk = { x: mkRaw.x - CIRCLE_OFFSET_X, y: mkRaw.y };

  // 위험 등급 장기(빨간 버튼) → 빨간 선 / 그 외 → #54A4CB 선
  const lineColor = riskOrgans.has(selectedOrgan) ? '#ed5454' : '#54A4CB';

  // 살짝 꺾인 선: 버튼에서 수평으로 가다가 꺾임점에서 마커로
  const bendX = b.x + (mk.x - b.x) * 0.55;
  const points = `${b.x},${b.y} ${bendX},${b.y} ${mk.x},${mk.y}`;

  return (
    <Box sx={{ position: 'absolute', inset: 0, zIndex: 3, pointerEvents: 'none' }}>
      <svg width={STAGE_WIDTH} height={STAGE_HEIGHT} style={{ overflow: 'visible' }}>
        <polyline
          points={points}
          fill="none"
          stroke={lineColor}
          strokeWidth={1.6}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <circle cx={b.x} cy={b.y} r={3} fill={lineColor} />
      </svg>
    </Box>
  );
};

const DiseaseSummary = ({
  diseases,
  onDiseaseClick,
}: {
  diseases: DiseaseRisk[];
  onDiseaseClick?: (disease: DiseaseRisk) => void;
}) => (
  <Box
    sx={{
      mx: 2,
      display: 'grid',
      gridTemplateColumns: 'repeat(4, minmax(68px, 1fr))',
      gap: '8px',
    }}
  >
    {diseases.map((disease) => (
      <ButtonBase
        key={disease.label}
        // 서버 판단 결과가 없으면 상세 시트를 열 게 없다
        disabled={!disease.detail}
        onClick={() => onDiseaseClick?.(disease)}
        sx={{
          height: 101,
          minWidth: 0,
          bgcolor: '#fff',
          borderRadius: '14px',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: '8px 7px 5px',
          boxSizing: 'border-box',
        }}
      >
        <Typography sx={{ fontSize: 15, color: '#333', lineHeight: 1.2, whiteSpace: 'nowrap' }}>
          {disease.label}
        </Typography>
        <Box
          sx={{
            width: '100%',
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'visible',
          }}
        >
          <Box
            component="img"
            src={disease.icon}
            alt={disease.label}
            sx={{
              width: 34,
              height: 34,
              objectFit: 'contain',
              display: 'block',
            }}
          />
        </Box>
        {/* 서버 등급이 있을 때만 색 배지를 띄운다 — 조회 전에는 회색 '—' */}
        <Box
          title={disease.detail ? disease.detail.riskGrade : '건강 데이터 미조회'}
          sx={{
            width: '100%',
            maxWidth: 68,
            height: 21,
            borderRadius: '50px',
            bgcolor: disease.detail ? STATUS_COLOR[disease.status] : '#dfe5ec',
            color: disease.detail ? '#fff' : '#98a5b6',
            fontSize: 13,
            fontWeight: 800,
            lineHeight: '21px',
          }}
        >
          {disease.detail ? disease.status : '—'}
        </Box>
      </ButtonBase>
    ))}
  </Box>
);

/**
 * 검진 지표 타일 4칸 — AP_009_002 의 user.healthIndicators 에서 만든다.
 * 4칸이라 3열 그리드에서는 둘째 줄에 한 칸만 남아, 2열(2×2)로 배치한다.
 *
 * 정상/비정상 색 구분은 넣지 않는다. 이 프로젝트의 정상범위 기준이 파일마다
 * 서로 모순된 상태라(deviceInfo.getPressureStatus 101~120 vs METRIC_DEF 90~120),
 * 여기서 새 기준을 정하면 같은 문제를 하나 더 만드는 셈이 된다.
 * 결측값은 '미측정'으로 흐리게 표시한다 — 없는 값을 0 이나 빈칸으로 보이게 하지 않는다.
 */
const VitalStats = ({ vitals }: { vitals: VitalMetricView[] }) => (
  <Box
    sx={{
      mx: 2,
      mt: 3.9,
      display: 'grid',
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
      gap: '13px 8px',
    }}
  >
    {vitals.map((metric) => (
      <Box
        key={metric.id}
        sx={{
          height: 64,
          textAlign: 'left',
          borderRadius: '14px',
          border: '1px solid rgba(255,255,255,.5)',
          bgcolor: metric.missing ? '#eaeef2' : '#e8ecf1',
          boxShadow: metric.missing ? 'none' : 'inset 4px 4px 4px #c5d3da',
          p: '10px 13px',
          boxSizing: 'border-box',
          cursor: 'default',
          pointerEvents: 'none', // 클릭 자체를 받지 않음
        }}
      >
        <Typography
          sx={{
            fontSize: 13,
            color: metric.missing ? '#98a5b6' : '#333',
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
          }}
        >
          {metric.label}
        </Typography>
        <Typography
          sx={{
            mt: 0.6,
            color: metric.missing ? '#a8b3c0' : '#54a4cb',
            fontSize: 16,
            fontWeight: metric.missing ? 500 : 800,
            textAlign: 'right',
            whiteSpace: 'nowrap',
          }}
        >
          {metric.value}
        </Typography>
      </Box>
    ))}
  </Box>
);

const Modeling = () => {
  const navigate = useNavigate();
  // 스테이지(3D 캐릭터 영역) 높이 — 화면 크기에 따라 반응형으로 조절
  const stageHeight = useStageHeight();
  // 회전 제스처를 받을 영역(모델 몸통 위) — 이 영역에서만 회전, 밖에서는 스크롤
  const [rotateZone, setRotateZone] = useState<HTMLDivElement | null>(null);
  const [drawerDisease, setDrawerDisease] = useState<DiseaseRisk | null>(null);
  const [selectedOrgan, setSelectedOrgan] = useState<string | null>(null);
  // 4대 질병 건강 문진 (AP_009_001)
  //  응답을 이 화면에 두어 시트를 닫았다 다시 열어도 유지된다.
  //  민감정보라 localStorage 에는 남기지 않는다 — 저장은 서버 전송으로만 한다.
  const [surveyOpen, setSurveyOpen] = useState(false);
  const [surveyAnswers, setSurveyAnswers] = useState<SurveyAnswers>(createEmptyAnswers);
  const [savedSurveyId, setSavedSurveyId] = useState<number | null>(null);
  const [surveySavedToast, setSurveySavedToast] = useState(false);
  // 질환·장기 판단 결과 (AP_009_002) — 화면 2·5 와 모듈 캐시를 공유한다
  const { data: twin, loading: twinLoading, error: twinError, reload: loadTwin } = useDigitalTwin();

  // 문진 작성 여부 (permission/isAuth 의 questionnaire)
  const {
    data: surveyStatus,
    loading: statusLoading,
    reload: reloadSurveyStatus,
  } = useSurveyStatus();

  /**
   * 이 계정이 문진을 이미 작성했는지. 판정 우선순위:
   *   1) 이 세션에서 방금 저장했다면 무조건 완료
   *   2) permission/isAuth 의 questionnaire — 백엔드와 합의된 기준.
   *      number 면 완료, null 이면 미작성.
   *   3) 그 필드가 아직 배포되지 않은 동안(undefined)만 AP_009_002 의 user.survey 로 임시 판정.
   *      questionnaire 가 내려오기 시작하면 이 폴백은 자동으로 쓰이지 않는다.
   */
  const surveyDone = useMemo(() => {
    if (savedSurveyId !== null) return true;
    const q = surveyStatus?.questionnaireId;
    if (q !== undefined) return q !== null;
    return hasSubmittedSurvey(twin);
  }, [savedSurveyId, surveyStatus, twin]);

  /**
   * 문진 미작성이 **확인된** 경우에만 true. 자동 오픈은 이 값으로만 판단한다.
   *
   * surveyDone 을 그대로 쓰면 안 되는 이유: surveyDone 은 "완료인가"라서
   * 판정 불가도 false(=미완료)로 뭉뚱그린다. 그런데 판정 불가는 여러 이유로 생긴다 —
   * 인증이 일시적으로 거부되면 isAuth 응답이 `{ message }` 하나뿐이라
   * questionnaire 키가 없고, 그때의 undefined 는 '미제출'이 아니다.
   * 그 상태에서 문진을 띄우면 이미 작성한 사용자가 다시 작성하게 되고,
   * AP_009_001 에 중복 방지가 없어 레코드가 그대로 하나 더 쌓인다(실측 확인).
   *
   * 그래서 확신이 없을 때는 띄우지 않는다. 사용자는 아래 '4대 질병 건강 문진'
   * 카드로 언제든 직접 열 수 있으므로, 안 띄워서 잃는 것보다 잘못 띄워서 잃는 것이 크다.
   */
  const surveyKnownMissing = useMemo(() => {
    if (savedSurveyId !== null) return false;
    const q = surveyStatus?.questionnaireId;
    if (q !== undefined) return q === null;        // 서버가 명시적으로 '미제출'
    if (surveyStatus?.authenticated !== true) return false; // 판정 불가 — 띄우지 않는다
    return !hasSubmittedSurvey(twin);              // questionnaire 미배포 서버용 폴백
  }, [savedSurveyId, surveyStatus, twin]);

  /**
   * 페이지 진입 시 미작성 계정에만 문진을 자동으로 띄운다 (아이디 당 1회).
   * 판정 근거가 모두 도착한 뒤 딱 한 번만 판단한다 — 닫았는데 다시 열리면 빠져나갈 수 없다.
   * isAuth 조회가 실패하면 위 3)번 폴백으로 판단하고 진행한다(무한 대기 방지).
   */
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpenedRef.current) return;
    if (twinLoading || twinError !== null || twin === null) return;
    if (statusLoading) return;
    autoOpenedRef.current = true;
    if (surveyKnownMissing) setSurveyOpen(true);
  }, [twin, twinLoading, twinError, statusLoading, surveyKnownMissing]);

  /** Grade 0 이 아닌 장기의 화면 키. 조회 실패 시 빈 Set → 위험 표시 없음 */
  const riskOrgans = useMemo(() => {
    const set = new Set<string>();
    if (!twin?.organ) return set;
    Object.keys(ORGAN_KO_NAME).forEach((value) => {
      if (isOrganAtRisk(twin.organ[ORGAN_KO_NAME[value]])) set.add(value);
    });
    return set;
  }, [twin]);

  const diseaseCards = useMemo<DiseaseRisk[]>(() => buildDiseaseCards(twin), [twin]);

  const vitals = useMemo<VitalMetricView[]>(
    () => buildVitalMetrics(twin?.user?.healthIndicators),
    [twin],
  );
  // 레이저 변신 효과 — dir 'up': 아래→위 / 'down': 위→아래, id로 매번 재생
  const [laser, setLaser] = useState<{ dir: 'up' | 'down'; id: number } | null>(null);
  // 캐릭터 한 바퀴 회전 트리거 (값이 바뀔 때마다 회전)
  const [spinNonce, setSpinNonce] = useState(0);
  const prevOrganRef = useRef<string | null>(null);
  const laserIdRef = useRef(0);
  // 애니메이션 진행 중 클릭 잠금 (클릭 1회만 실행)
  const busyRef = useRef(false);

  useEffect(() => {
    const prev = prevOrganRef.current;
    let dir: 'up' | 'down' | null = null;
    if (!prev && selectedOrgan) {
      dir = 'up'; // 장기 선택 → 아래에서 위로
    } else if (prev && !selectedOrgan) {
      dir = 'down'; // 원점 복귀 → 위에서 아래로
    } else if (prev && selectedOrgan && prev !== selectedOrgan) {
      dir = 'up'; // 다른 장기로 전환 → 아래에서 위로 (선택과 동일)
    }
    if (dir) {
      laserIdRef.current += 1;
      setLaser({ dir, id: laserIdRef.current });
      setSpinNonce((n) => n + 1); // 선택·전환·복귀 모두 캐릭터 한 바퀴 회전
    }
    prevOrganRef.current = selectedOrgan;
  }, [selectedOrgan]);

  useEffect(() => {
    if (!laser) return;
    const t = window.setTimeout(() => setLaser(null), 2600);
    return () => window.clearTimeout(t);
  }, [laser]);

  const organButtons: OrganButton[] = [
    { value: 'brain', iconRed: iconBrainRed, iconBlue: iconBrainBlue, label: '뇌' },
    { value: 'heart', iconRed: iconHeartRed, iconBlue: iconHeartBlue, label: '심장' },
    { value: 'kidneys', iconRed: iconKidneysRed, iconBlue: iconKidneysBlue, label: '신장' },
    { value: 'liver', iconRed: iconLiverRed, iconBlue: iconLiverBlue, label: '간' },
    { value: 'intestine', iconRed: iconIntestineRed, iconBlue: iconIntestineBlue, label: '대장' },
    { value: 'pancreas', iconRed: iconPancreasRed, iconBlue: iconPancreasBlue, label: '췌장' },
  ];

  // 왼쪽 버튼 클릭 → 토글 (선택 / 한 번 더 누르면 원점 해제)
  //  애니메이션(빛줄기 3초) 진행 중에는 연타·중복 클릭 무시 → 1회만 실행
  const handleOrganSelect = (organ: OrganButton) => {
    if (busyRef.current) return;
    busyRef.current = true;
    window.setTimeout(() => {
      busyRef.current = false;
    }, 3000);
    setSelectedOrgan((prev) => (prev === organ.value ? null : organ.value));
  };

  // 오른쪽 마커(원) 클릭 → 화면 2로 이동
  //  단, 먼저 왼쪽 장기 버튼으로 그 장기를 선택해 둔 경우에만 동작
  const handleMarkerClick = (organ: string) => {
    if (organ !== selectedOrgan) return; // 미선택 상태이거나 다른 장기 마커면 무시
    navigate(`/digitalTwin/screen2?organ=${organ}`);
  };

  const surveyProgress = countAllAnswered(surveyAnswers);

  return (
    <Box sx={{ bgcolor: '#ecf0f2', minHeight: '100vh' }}>
      <Container
        maxWidth={false}
        sx={{
          position: 'relative',
          maxWidth: '430px !important',
          minWidth: 320,
          minHeight: '100vh',
          mx: 'auto',
          p: '0 !important',
          pb: '90px !important',
          bgcolor: '#ecf0f2',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            mx: 2,
            mt: 1.2,
            height: stageHeight,
          }}
        >
          {/* 3D 캐릭터 모델 (z-index 1) — 장기 선택 시 천천히 투명, 회전 잠금 */}
          <Box
            sx={{
              position: 'absolute',
              inset: '-2px -40px 0 36px',
              zIndex: 1,
              filter: 'saturate(0.9) brightness(1.04)',
              opacity: selectedOrgan ? 0.18 : 1,
              // 회전·빛줄기 속도(3초)에 맞춰 천천히 투명/복귀
              transition: 'opacity 3s ease',
              // 캔버스 자체는 터치를 받지 않는다 — 회전은 아래 rotateZone 에서만 받고,
              // 그 밖의 터치는 페이지로 흘러가 스크롤된다.
              pointerEvents: 'none',
            }}
          >
            <Suspense fallback={modelLoading}>
              <ModelViewer
                expression="neutral"
                spinSignal={spinNonce}
                frozen={!!selectedOrgan}
                rotateEl={rotateZone}
              />
            </Suspense>
          </Box>
          {/* 회전 영역 (z-index 2) — 모델 몸통 위에만 얹는다. 첫 화면(미선택)에서만 표시.
              이 영역 안: 좌우 드래그로 회전 / 이 영역 밖: 터치가 페이지로 흘러 스크롤. */}
          {!selectedOrgan && (
            <Box
              ref={setRotateZone}
              sx={{
                position: 'absolute',
                // 스테이지 기준 중앙 몸통 영역 — 좌우/상하에 스크롤용 여백을 남긴다
                left: '27%',
                right: '21%',
                top: '3%',
                bottom: '5%',
                zIndex: 2,
                cursor: 'grab',
                '&:active': { cursor: 'grabbing' },
              }}
            />
          )}
          {/* 레이저 변신 효과 — 원통형 빛줄기 (선택: 아래→위 / 전환·복귀: 위→아래) */}
          {laser && (
            <Box
              key={laser.id}
              sx={{
                position: 'absolute',
                inset: '-2px -40px 0 36px',
                zIndex: 5,
                pointerEvents: 'none',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  // 캐릭터 폭만큼 — 캐릭터를 감싸는 타원 링
                  left: '13%',
                  right: '13%',
                  height: 70,
                  // 납작한 타원 → 원기둥을 비스듬히 본 단면
                  borderRadius: '50%',
                  // 앞쪽(아래) 호는 진하게, 뒤쪽(위) 중앙은 완전히 안 보이게
                  border: '7px solid',
                  borderTopColor: 'transparent',
                  borderBottomColor: 'rgba(214,243,255,.95)',
                  borderLeftColor: 'rgba(214,243,255,.45)',
                  borderRightColor: 'rgba(214,243,255,.45)',
                  background: 'transparent',
                  // 그림자도 앞쪽(아래)만 — 위쪽은 spread 음수로 거의 안 보이게
                  boxShadow: '0 9px 12px -5px rgba(140,216,255,.6)',
                  filter: 'blur(1.4px)',
                  animation: `${laser.dir === 'up' ? 'laserUp' : 'laserDown'} 3s ease-in-out`,
                  '@keyframes laserUp': {
                    '0%': { bottom: '-32px', opacity: 0 },
                    '12%': { opacity: 1 },
                    '88%': { opacity: 1 },
                    '100%': { bottom: '100%', opacity: 0 },
                  },
                  '@keyframes laserDown': {
                    '0%': { top: '-32px', opacity: 0 },
                    '12%': { opacity: 1 },
                    '88%': { opacity: 1 },
                    '100%': { top: '100%', opacity: 0 },
                  },
                }}
              />
            </Box>
          )}
          {/* 선택 장기 3D 모델 (z-index 2) — 빛줄기가 지나간 뒤 나타남.
              모델을 눌러도 마커(원)와 동일하게 화면 2로 이동 */}
          <OrganModelLayer
            key={selectedOrgan ?? 'none'}
            selectedOrgan={selectedOrgan}
            onModelClick={handleMarkerClick}
          />
          {/* 마커 + 퍼짐 효과 (z-index 3, 마커 클릭 시 화면 2 이동) */}
          <RiskMarkers
            selectedOrgan={selectedOrgan}
            onMarkerClick={handleMarkerClick}
            riskOrgans={riskOrgans}
          />
          {/* 좌측 장기 박스 레일 (z-index 4) */}
          <OrganRail
            organs={organButtons}
            selectedOrgan={selectedOrgan}
            onSelect={handleOrganSelect}
            riskOrgans={riskOrgans}
          />
        </Box>

        {/*
          4대 질병 건강 문진 진입 — 위험도 산출의 보조 지표를 사용자에게 직접 받는다.
          아이디 당 1회이므로 작성을 마친 계정에서는 눌리지 않는 완료 표시로만 남는다.
        */}
        <ButtonBase
          onClick={() => setSurveyOpen(true)}
          disabled={surveyDone}
          sx={{
            mx: 2,
            mt: 1.4,
            mb: 1.4,
            width: 'calc(100% - 32px)',
            minHeight: 62,
            bgcolor: '#fff',
            borderRadius: '15px',
            p: '13px 15px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            textAlign: 'left',
            opacity: surveyDone ? 0.85 : 1,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: 15.5, fontWeight: 700, color: '#333', lineHeight: 1.3 }}>
              4대 질병 건강 문진
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: '#8c9aab', mt: '3px', lineHeight: 1.35 }}>
              {surveyDone
                ? '작성 완료 — 문진은 계정당 한 번만 작성합니다'
                : surveyProgress.answered === 0
                  ? `당뇨병 · 고혈압 · 이상지질혈증 · 비만 ${surveyProgress.total}문항`
                  : `작성 중 ${surveyProgress.answered} / ${surveyProgress.total}문항`}
            </Typography>
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              ml: 1.2,
              px: 1.4,
              height: 32,
              borderRadius: '99px',
              display: 'flex',
              alignItems: 'center',
              fontSize: 13,
              fontWeight: 800,
              bgcolor: surveyDone ? '#e8f6ee' : '#eef1f8',
              color: surveyDone ? '#2f9e63' : '#6e8df0',
            }}
          >
            {surveyDone ? '완료' : surveyProgress.answered === 0 ? '문진 시작' : '이어서 작성'}
          </Box>
        </ButtonBase>

        {/* 조회 상태 — 실패 시 목업 수치를 대신 보여주지 않고 상태를 그대로 알린다 */}
        {(twinLoading || twinError !== null) && (
          <Box
            sx={{
              mx: 2,
              mb: 1.4,
              px: 1.6,
              py: 1.2,
              borderRadius: '12px',
              bgcolor: twinError ? '#fdeaea' : '#e8ecf1',
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
            }}
          >
            {twinLoading && <CircularProgress size={16} sx={{ color: '#8aa2ad' }} />}
            <Typography
              sx={{ flex: 1, fontSize: 12.5, lineHeight: 1.45, color: twinError ? '#c2494e' : '#5a6b80' }}
            >
              {twinLoading ? '건강 데이터를 불러오는 중입니다…' : twinError}
            </Typography>
            {!twinLoading && (
              <ButtonBase
                onClick={loadTwin}
                sx={{
                  flexShrink: 0,
                  px: 1.4,
                  height: 28,
                  borderRadius: '99px',
                  bgcolor: '#fff',
                  color: '#c2494e',
                  fontSize: 12.5,
                  fontWeight: 800,
                }}
              >
                다시 시도
              </ButtonBase>
            )}
          </Box>
        )}

        <DiseaseSummary
          diseases={diseaseCards}
          onDiseaseClick={(disease) => setDrawerDisease(disease)}
        />

        <VitalStats vitals={vitals} />

        {/* 하단 안내 문구 (화면 1~5 공통) */}
        <Disclaimer />
      </Container>

      <DigitalTwinScreen4
        open={drawerDisease !== null}
        disease={drawerDisease}
        onClose={() => setDrawerDisease(null)}
      />
      {/* 저장 성공 Snackbar 를 시트 안에서 보여줘야 하므로 onSaved 에서 닫지 않는다 */}
      <DiseaseSurveySheet
        open={surveyOpen}
        onClose={() => setSurveyOpen(false)}
        answers={surveyAnswers}
        onChangeAnswers={setSurveyAnswers}
        onSaved={(surveyId) => {
          setSavedSurveyId(surveyId);
          setSurveyOpen(false); // 아이디 당 1회이므로 저장되면 닫는다
          setSurveySavedToast(true);
          // 제출 여부(isAuth.questionnaire)를 다시 읽어 다음 진입부터 '완료'로 잠기게 한다
          reloadSurveyStatus();
          // 문진 결과는 판단 근거에 들어간다 — 스펙의 missingData 에 '문진/가족력'이 있다.
          // 저장 직후 재조회해 등급·신뢰도·커버리지를 갱신한다.
          loadTwin();
        }}
      />
      <Snackbar
        open={surveySavedToast}
        autoHideDuration={3500}
        onClose={() => setSurveySavedToast(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ zIndex: 1400 }}
      >
        <Alert severity="success" onClose={() => setSurveySavedToast(false)} sx={{ fontSize: 13.5 }}>
          건강 문진이 저장되었습니다. 판단 결과를 다시 불러옵니다.
        </Alert>
      </Snackbar>
      <Bottom />
    </Box>
  );
};

export default Modeling;
